import React, { useState, useEffect } from 'react';
import { Building2, TrendingUp, TrendingDown, DollarSign, RefreshCw, ChevronRight, Minus } from 'lucide-react';
import axios from 'axios';
import Pagination from '@/components/Pagination';

const BankRegister = () => {
  const [bankData, setBankData] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedBank, setSelectedBank] = useState('all');
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [withdrawData, setWithdrawData] = useState({
    amount: '',
    bank_id: '',
    withdrawal_date: new Date().toISOString().split('T')[0],
    reference_number: '',
    notes: ''
  });
  const [filters, setFilters] = useState({
    from_date: '', // Empty for all time by default
    to_date: new Date().toISOString().split('T')[0], // Today
    transaction_type: 'all'
  });
  const [summary, setSummary] = useState({ total_credits: 0, total_debits: 0, balance: 0 });
  
  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [totalItems, setTotalItems] = useState(0);

  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchBankBalances();
  }, []);

  useEffect(() => {
    fetchTransactions();
  }, [currentPage, itemsPerPage, selectedBank]);

  const fetchBankBalances = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/bank-register/balance`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBankData(response.data);
    } catch (error) {
      console.error('Error fetching bank balances:', error);
    }
  };

  const fetchTransactions = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedBank !== 'all') params.append('bank_id', selectedBank);
      if (filters.from_date) params.append('from_date', filters.from_date);
      if (filters.to_date) params.append('to_date', filters.to_date);
      if (filters.transaction_type !== 'all') params.append('transaction_type', filters.transaction_type);
      params.append('page', currentPage);
      params.append('limit', itemsPerPage);

      const response = await axios.get(`${API_URL}/api/bank-register/transactions?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Handle paginated response
      setTransactions(response.data.transactions || []);
      setTotalItems(response.data.total || response.data.transactions?.length || 0);
      setSummary(response.data.summary || { total_credits: 0, total_debits: 0, balance: 0 });
    } catch (error) {
      console.error('Error fetching transactions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  const handleFilterApply = () => {
    setCurrentPage(1);
    fetchTransactions();
  };

  // Quick date range filters
  const setDateRange = (range) => {
    const today = new Date();
    let fromDate, toDate;

    switch(range) {
      case 'today':
        fromDate = toDate = today.toISOString().split('T')[0];
        break;
      case 'yesterday':
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        fromDate = toDate = yesterday.toISOString().split('T')[0];
        break;
      case 'this_week':
        const weekStart = new Date(today);
        weekStart.setDate(today.getDate() - today.getDay());
        fromDate = weekStart.toISOString().split('T')[0];
        toDate = today.toISOString().split('T')[0];
        break;
      case 'this_month':
        fromDate = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0];
        toDate = today.toISOString().split('T')[0];
        break;
      case 'last_month':
        const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);
        fromDate = lastMonth.toISOString().split('T')[0];
        toDate = lastMonthEnd.toISOString().split('T')[0];
        break;
      case 'this_year':
        fromDate = new Date(today.getFullYear(), 0, 1).toISOString().split('T')[0];
        toDate = today.toISOString().split('T')[0];
        break;
      case 'all_time':
        fromDate = '';
        toDate = '';
        break;
      default:
        return;
    }

    setFilters(prev => ({ ...prev, from_date: fromDate, to_date: toDate }));
  };

  useEffect(() => {
    if (bankData) {
      fetchTransactions();
    }
  }, [selectedBank, filters]);

  const handleWithdrawFromBank = async (e) => {
    e.preventDefault();
    
    if (!withdrawData.bank_id || !withdrawData.amount || parseFloat(withdrawData.amount) <= 0) {
      alert('Please select a bank and enter a valid amount');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/bank-register/withdraw-from-bank`, {
        ...withdrawData,
        amount: parseFloat(withdrawData.amount)
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      alert(response.data.message + '\n' + response.data.note);
      setShowWithdrawModal(false);
      setWithdrawData({
        amount: '',
        bank_id: '',
        withdrawal_date: new Date().toISOString().split('T')[0],
        reference_number: '',
        notes: ''
      });
      fetchBankBalances();
      fetchTransactions();
    } catch (error) {
      console.error('Error withdrawing cash:', error);
      alert(error.response?.data?.detail || 'Failed to record cash withdrawal. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getTransactionTypeColor = (type) => {
    return type === 'Credit' ? 'text-green-600' : 'text-red-600';
  };

  const getTransactionBadgeColor = (type) => {
    return type === 'Credit' 
      ? 'bg-green-100 text-green-800' 
      : 'bg-red-100 text-red-800';
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Bank Register</h1>
          <p className="text-sm text-gray-500 mt-1">Track bank accounts and transactions</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => {
              fetchBankBalances();
              fetchTransactions();
            }}
            className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
          >
            <RefreshCw className="h-4 w-4" />
            Refresh
          </button>
          <button
            onClick={() => setShowWithdrawModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
          >
            <Minus className="h-4 w-4" />
            Withdraw from Bank
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 p-6 rounded-lg shadow-lg text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-blue-100">Total Bank Balance</p>
              <p className="text-3xl font-bold mt-1">
                ₹{bankData ? bankData.total_current_balance.toLocaleString('en-IN') : '0'}
              </p>
              <p className="text-xs text-blue-100 mt-1">
                {bankData?.banks_count || 0} banks
              </p>
            </div>
            <Building2 className="h-12 w-12 text-blue-200" />
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Opening Balance</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                ₹{bankData ? bankData.total_opening_balance.toLocaleString('en-IN') : '0'}
              </p>
            </div>
            <DollarSign className="h-10 w-10 text-gray-500" />
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Credits</p>
              <p className="text-2xl font-bold text-green-600 mt-1">
                ₹{bankData ? bankData.total_credits.toLocaleString('en-IN') : '0'}
              </p>
              <p className="text-xs text-gray-400 mt-1">
                {bankData?.credits_count || 0} transactions
              </p>
            </div>
            <TrendingUp className="h-10 w-10 text-green-500" />
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Debits</p>
              <p className="text-2xl font-bold text-red-600 mt-1">
                ₹{bankData ? bankData.total_debits.toLocaleString('en-IN') : '0'}
              </p>
              <p className="text-xs text-gray-400 mt-1">
                {bankData?.debits_count || 0} transactions
              </p>
            </div>
            <TrendingDown className="h-10 w-10 text-red-500" />
          </div>
        </div>
      </div>

      {/* Bank Accounts List */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-4 border-b">
          <h3 className="text-lg font-semibold">Bank Accounts</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4">
          {bankData?.banks.map((bank) => (
            <div
              key={bank.id}
              className="border rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => setSelectedBank(selectedBank === bank.id ? 'all' : bank.id)}
            >
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h4 className="font-semibold text-gray-900">{bank.bank_name}</h4>
                    {selectedBank === bank.id && (
                      <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                        Selected
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-600 mt-1">A/C: {bank.account_number}</p>
                  {bank.branch && (
                    <p className="text-xs text-gray-500 mt-1">Branch: {bank.branch}</p>
                  )}
                  
                  <div className="mt-3 grid grid-cols-2 gap-2">
                    <div>
                      <p className="text-xs text-gray-500">Opening</p>
                      <p className="text-sm font-semibold text-gray-700">
                        ₹{bank.opening_balance.toLocaleString('en-IN')}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Current</p>
                      <p className="text-lg font-bold text-blue-600">
                        ₹{bank.current_balance.toLocaleString('en-IN')}
                      </p>
                    </div>
                  </div>

                  <div className="mt-2 flex gap-4 text-xs text-gray-600">
                    <span className="text-green-600">↑ {bank.credits_count} credits</span>
                    <span className="text-red-600">↓ {bank.debits_count} debits</span>
                  </div>
                </div>
                <ChevronRight className={`h-5 w-5 text-gray-400 transition-transform ${
                  selectedBank === bank.id ? 'rotate-90' : ''
                }`} />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4">
        <h3 className="text-lg font-semibold mb-4">Filter Transactions</h3>
        
        {/* Quick Filter Buttons */}
        <div className="mb-4">
          <p className="text-sm font-medium text-gray-700 mb-2">Quick Filters:</p>
          <div className="flex flex-wrap gap-2">
            <button onClick={() => setDateRange('all_time')} className="px-3 py-1 text-sm bg-green-500 text-white rounded hover:bg-green-600 font-medium">All Time</button>
            <button onClick={() => setDateRange('today')} className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded hover:bg-blue-200">Today</button>
            <button onClick={() => setDateRange('yesterday')} className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded hover:bg-blue-200">Yesterday</button>
            <button onClick={() => setDateRange('this_week')} className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded hover:bg-blue-200">This Week</button>
            <button onClick={() => setDateRange('this_month')} className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded hover:bg-blue-200">This Month</button>
            <button onClick={() => setDateRange('last_month')} className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded hover:bg-blue-200">Last Month</button>
            <button onClick={() => setDateRange('this_year')} className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded hover:bg-blue-200">This Year</button>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Bank Account</label>
            <select
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              value={selectedBank}
              onChange={(e) => setSelectedBank(e.target.value)}
            >
              <option value="all">All Banks</option>
              {bankData?.banks.map((bank) => (
                <option key={bank.id} value={bank.id}>
                  {bank.bank_name} - {bank.account_number}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">From Date</label>
            <input
              type="date"
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              value={filters.from_date}
              onChange={(e) => setFilters({ ...filters, from_date: e.target.value })}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">To Date</label>
            <input
              type="date"
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              value={filters.to_date}
              onChange={(e) => setFilters({ ...filters, to_date: e.target.value })}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Type</label>
            <select
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              value={filters.transaction_type}
              onChange={(e) => setFilters({ ...filters, transaction_type: e.target.value })}
            >
              <option value="all">All Transactions</option>
              <option value="Credit">Credits Only</option>
              <option value="Debit">Debits Only</option>
            </select>
          </div>
        </div>
      </div>

      {/* Transactions Table */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-4 border-b flex justify-between items-center">
          <h3 className="text-lg font-semibold">Bank Transactions</h3>
          <span className="text-sm text-gray-500">
            {transactions.length} transactions
            {selectedBank !== 'all' && ' (filtered)'}
          </span>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Bank</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Payment Mode</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {transactions.map((txn, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm">{txn.transaction_date}</td>
                  <td className="px-4 py-3 text-sm font-medium">{txn.bank_name || 'N/A'}</td>
                  <td className="px-4 py-3 text-sm">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getTransactionBadgeColor(txn.transaction_type)}`}>
                      {txn.transaction_type}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm">{txn.category}</td>
                  <td className="px-4 py-3 text-sm text-gray-600 max-w-xs truncate">{txn.description}</td>
                  <td className="px-4 py-3 text-sm">{txn.payment_mode}</td>
                  <td className={`px-4 py-3 text-sm text-right font-semibold ${getTransactionTypeColor(txn.transaction_type)}`}>
                    {txn.transaction_type === 'Credit' ? '+' : '-'}₹{(txn.amount || 0).toLocaleString('en-IN')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {transactions.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              {loading ? 'Loading transactions...' : 'No transactions found'}
            </div>
          )}
        </div>
        
        {/* Pagination */}
        <Pagination
          currentPage={currentPage}
          totalPages={Math.ceil(totalItems / itemsPerPage)}
          totalItems={totalItems}
          itemsPerPage={itemsPerPage}
          onPageChange={handlePageChange}
          onItemsPerPageChange={handleItemsPerPageChange}
        />
      </div>

      {/* Withdraw from Bank Modal */}
      {showWithdrawModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-xl font-bold mb-4 text-red-600">Withdraw Cash from Bank</h3>
            <form onSubmit={handleWithdrawFromBank}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select Bank *
                  </label>
                  <select
                    value={withdrawData.bank_id}
                    onChange={(e) => setWithdrawData({ ...withdrawData, bank_id: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500"
                    required
                  >
                    <option value="">-- Select Bank --</option>
                    {bankData?.banks.map(bank => (
                      <option key={bank.id} value={bank.id}>
                        {bank.bank_name} - {bank.account_number} (Balance: ₹{bank.current_balance.toLocaleString('en-IN')})
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Amount *
                  </label>
                  <input
                    type="number"
                    value={withdrawData.amount}
                    onChange={(e) => setWithdrawData({ ...withdrawData, amount: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500"
                    placeholder="Enter amount"
                    step="0.01"
                    required
                  />
                  {withdrawData.bank_id && bankData?.banks.find(b => b.id === withdrawData.bank_id) && (
                    <p className="text-xs text-gray-500 mt-1">
                      Available: ₹{bankData.banks.find(b => b.id === withdrawData.bank_id).current_balance.toLocaleString('en-IN')}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Withdrawal Date
                  </label>
                  <input
                    type="date"
                    value={withdrawData.withdrawal_date}
                    onChange={(e) => setWithdrawData({ ...withdrawData, withdrawal_date: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Reference Number
                  </label>
                  <input
                    type="text"
                    value={withdrawData.reference_number}
                    onChange={(e) => setWithdrawData({ ...withdrawData, reference_number: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500"
                    placeholder="Cheque number / Transaction ID"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Notes
                  </label>
                  <textarea
                    value={withdrawData.notes}
                    onChange={(e) => setWithdrawData({ ...withdrawData, notes: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500"
                    rows="3"
                    placeholder="Purpose of withdrawal"
                  />
                </div>

                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  <p className="text-xs text-yellow-800">
                    💡 This will decrease bank balance and increase cash balance. The withdrawn amount will be available in Cash Register.
                  </p>
                </div>
              </div>

              <div className="flex gap-2 mt-6">
                <button
                  type="button"
                  onClick={() => setShowWithdrawModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:bg-gray-400"
                >
                  {loading ? 'Processing...' : 'Record Withdrawal'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default BankRegister;
